'use strict';
/*
  vhs — 80s/90s home-video. Warm grain, heavy scanlines, tracking jitter, RGB
  split, a blinking REC dot + timecode, soft chroma wobble on the beat.
*/
const { layoutCue } = require('./_common');
const { FONTS } = require('../lib/draw');

const palette = { bg: '#0d0d0a', fg: '#f4efe6', accent: '#ff5a5a', accent2: '#6cf0ff' };

function init(st){
  st.s.font = st.tk.registerFamily('ForgeVHS', FONTS.arialBlack) || undefined;
  st.s.mono = st.tk.registerFamily('ForgeMono', FONTS.mono) || undefined;
  st.s.track = 0; st.s.jolt = 0;
}
function onBeat(st, power){ st.s.jolt = Math.min(1, st.s.jolt + power * 0.4); }

function tc(st){ // timecode HH:MM:SS:FF based on virtual clock
  const tot = st.t, ff = Math.floor((tot % 1) * st.fps);
  const s = Math.floor(tot) % 60, m = Math.floor(tot / 60) % 60, h = Math.floor(tot / 3600);
  const p = n => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(s)}:${p(ff)}`;
}

function background(ctx, st){
  const { W, H } = st;
  st.s.jolt *= 0.85; st.s.track += 0.6;
  // warm wash — opaque only on solid-bg modes; translucent grade over footage
  if (!st.overlayMode) { ctx.fillStyle = '#0d0d0a'; ctx.fillRect(0, 0, W, H); }
  const g = ctx.createLinearGradient(0, 0, 0, H);
  const a = st.overlayMode ? 0.22 : 0.4;
  g.addColorStop(0, `rgba(40,20,10,${a})`); g.addColorStop(0.5, `rgba(10,15,20,${a*0.3})`); g.addColorStop(1, `rgba(30,10,30,${a})`);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  // a drifting tracking band of noise — bass-driven for music, gentle/static for talk
  const band = (st.s.track % H), bassN = st.reactive ? st.b / 255 : 0;
  ctx.save(); ctx.globalAlpha = 0.06 + bassN * 0.08; ctx.fillStyle = '#fff';
  const streaks = 18 + Math.floor(bassN * 30);
  for (let i = 0; i < streaks; i++) ctx.fillRect(Math.random()*W, band + Math.random()*60 - 30, Math.random()*W*0.5, 2);
  ctx.restore();
  // reactive bottom waveform line (tape audio readout — music only)
  if (st.spectrum && st.reactive) {
    const { NB, data } = st.spectrum, f = st.f, y0 = H * 0.9, bw = W / NB;
    ctx.save(); ctx.globalAlpha = st.overlayMode ? 0.4 : 0.6; ctx.strokeStyle = palette.accent2; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < NB; i++) { const v = (data[f * NB + i] || 0) / 255; const x = i * bw + bw / 2, y = y0 - (v - 0.5) * 90; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }
    ctx.stroke(); ctx.restore();
  }
}

function paintToken(ctx, o){
  const { T, cx, cy, px, scl, alpha, emph, st } = o;
  const tk = st.tk;
  const split = 2.5 + st.s.jolt * 6;
  ctx.save(); ctx.globalAlpha = alpha; ctx.translate(cx + (Math.random()-0.5)*st.s.jolt*4, cy); ctx.scale(scl, scl);
  ctx.font = tk.font(px, 900, st.s.font); ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = 'rgba(255,40,40,0.7)'; ctx.fillText(T, -split, 0);
  ctx.fillStyle = 'rgba(40,255,255,0.7)'; ctx.fillText(T, split, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = emph ? palette.accent : '#f4efe6';
  ctx.fillText(T, 0, 0);
  ctx.restore();
}

function drawCue(ctx, st){
  const anchor = st.mode === 'talk' ? 0.74 : 'center';
  layoutCue(ctx, st, { sizes: { big: 132, mid: 108, small: 64 }, anchor, safeW: 0.88, reveal: 0.24, perLetter: true, letterStagger: 0.022, trans: 0.18, family: st.s.font, paintToken });
}

function overlay(ctx, st){
  const { W, H, tk } = st;
  ctx.save(); ctx.globalAlpha = 0.5; ctx.drawImage(tk.scanlines(0.55, 3), 0, 0); ctx.restore();
  ctx.save(); ctx.globalAlpha = 0.06; ctx.globalCompositeOperation = 'screen'; ctx.drawImage(tk.grain(), -Math.random()*100, -Math.random()*100); ctx.restore();
  ctx.drawImage(tk.vignette(0.8, 280), 0, 0);
  // REC + timecode HUD (mono)
  ctx.save(); ctx.font = tk.font(34, 700, st.s.mono); ctx.textBaseline = 'middle';
  if (Math.floor(st.t * 2) % 2 === 0) { ctx.fillStyle = palette.accent; ctx.beginPath(); ctx.arc(60, 70, 14, 0, 7); ctx.fill(); }
  ctx.fillStyle = '#f4efe6'; ctx.textAlign = 'left'; ctx.fillText('REC', 86, 70);
  ctx.textAlign = 'right'; ctx.fillText(tc(st), W - 50, 70);
  ctx.textAlign = 'left'; ctx.font = tk.font(30, 700, st.s.mono); ctx.fillText('SP  ▶ PLAY', 50, H - 66);
  // reactive VU meter (top-right under timecode — music only)
  if (st.reactive) {
    const vu = st.m / 255, segs = 14, sw = 16, sh = 10;
    for (let i = 0; i < segs; i++) {
      const on = i / segs < vu; const x = W - 50 - (segs - i) * (sw + 4), y = 104;
      ctx.fillStyle = on ? (i > segs * 0.8 ? palette.accent : palette.accent2) : 'rgba(255,255,255,0.14)';
      ctx.fillRect(x, y, sw, sh);
    }
  }
  ctx.restore();
}

module.exports = { id: 'vhs', label: 'VHS · retro home video', palette, init, background, drawCue, overlay, onBeat, paintToken };
